/**
 * Custom hook to manage single day event state and logic
 *
 * @returns {Object} An object containing state and functions for managing single day events
 *
 * @process
 * 1. Initializes state from Redux store (eventData)
 * 2. Sets up form validation using Formik and Yup
 * 3. Implements validation logic for event times and slots
 * 4. Provides functions to manipulate slots (add/delete)
 * 5. Handles form submission by formatting data and dispatching Redux action
 * 6. Checks if event data has changed before submitting
 * 7. Manages slot duration calculations and validations
 *
 * Error handling:
 * - Validates dates are not in the past
 * - Validates time sequences (end time after start time)
 * - Validates slot times against event times
 * - Checks for slot overlaps
 * - Validates minimum number of slots when enabled
 * - Validates minimum slot duration (5 minutes)
 */
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import { updateEventById, nextTab } from '../../../../../../app/store/slices/event/event';
import { errorToast } from '../../../../../../utils/toasts/toasts';

export default function useSingleDayEvent() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { eventData } = useSelector((state) => state.event);
    const [minutes, setMinutes] = useState(30);
    const [hour, setHour] = useState('minutes');
    const [expanded, setExpanded] = useState(true);
    const [customDuration, setCustomDuration] = useState('');
    const [durationError, setDurationError] = useState(false);
    const [slotDuration, setSlotDuration] = useState(slotDurationOptions[0].value);

    const [loading, setLoading] = useState(false);

    function hasSingleDayEventChanged(formValues, eventData) {
        const cleaned = {
            start_date: dayjs(formValues.startDate).format('YYYY-MM-DD'),
            end_date: dayjs(formValues.endDate || formValues.startDate).format('YYYY-MM-DD'),
            start_time: dayjs(formValues.startTime, 'HH:mm').format('HH:mm:ss'),
            end_time: dayjs(formValues.endTime, 'HH:mm').format('HH:mm:ss'),
            isEventHasSlot: formValues.isEventHasSlot,
            slots: formValues.isEventHasSlot
                ? formValues.slots?.map((slot) => ({
                      from_time: dayjs(slot.startTime, 'HH:mm').format('HH:mm:ss'),
                      to_time: dayjs(slot.endTime, 'HH:mm').format('HH:mm:ss')
                  }))
                : []
        };

        const original = {
            start_date: dayjs(eventData?.start_date).format('YYYY-MM-DD'),
            end_date: dayjs(eventData?.end_date || eventData?.start_date).format('YYYY-MM-DD'),
            start_time: dayjs(eventData?.start_time, 'HH:mm').format('HH:mm:ss'),
            end_time: dayjs(eventData?.end_time, 'HH:mm').format('HH:mm:ss'),
            isEventHasSlot: eventData?.schedules?.[0]?.slots?.length > 0,
            slots:
                eventData?.schedules?.[0]?.slots?.map((slot) => ({
                    from_time: dayjs(slot.from_time, 'HH:mm').format('HH:mm:ss'),
                    to_time: dayjs(slot.to_time, 'HH:mm').format('HH:mm:ss')
                })) || []
        };

        return !isEqual(cleaned, original);
    }

    const initialValues = {
        startDate:
            eventData?.event_type === 'single_day' && eventData?.start_date
                ? dayjs(eventData.start_date)
                : null,
        endDate:
            eventData?.event_type === 'single_day' && eventData?.end_date
                ? dayjs(eventData.end_date)
                : null,
        startTime:
            eventData?.event_type === 'single_day' && eventData?.start_time
                ? dayjs(eventData.start_time, 'HH:mm')
                : null,
        endTime:
            eventData?.event_type === 'single_day' && eventData?.end_time
                ? dayjs(eventData.end_time, 'HH:mm')
                : null,
        isEventHasSlot:
            eventData?.event_type === 'single_day' && eventData?.schedules?.[0]?.slots?.length > 0
                ? true
                : false,
        slots:
            eventData?.event_type === 'single_day' && eventData?.schedules?.[0]?.slots
                ? eventData.schedules[0].slots.map((slot) => ({
                      id: slot?.slotId,
                      startTime: slot.from_time ? dayjs(slot.from_time, 'HH:mm') : null,
                      endTime: slot.to_time ? dayjs(slot.to_time, 'HH:mm') : null
                  }))
                : []
    };

    // const initialValues = useMemo(() => {
    //     if (!eventData) return {}; // Return an empty object if eventData is not available

    //     return {
    //         startDate: eventData?.start_date ? dayjs(eventData.start_date) : null,
    //         endDate: eventData?.end_date ? dayjs(eventData.end_date) : null,
    //         startTime: eventData?.start_time ? dayjs(eventData.start_time, 'HH:mm') : null,
    //         endTime: eventData?.end_time ? dayjs(eventData.end_time, 'HH:mm') : null,
    //         isEventHasSlot: eventData?.schedules?.length > 0 ? true : false,
    //         slots: eventData?.schedules?.length > 0
    //             ? eventData.schedules[0]?.slots?.map((slot, index) => ({
    //                 id: index + 1,
    //                 startTime: slot.from_time ? dayjs(slot.from_time, 'HH:mm') : null,
    //                 endTime: slot.to_time ? dayjs(slot.to_time, 'HH:mm') : null,
    //             }))
    //             : []
    //     };
    // }, [eventData]);

    // console.log("Single Day Event Initial Values:", initialValues);

    const singleDayEventFormik = useFormik({
        initialValues,
        validationSchema,
        validate: (values) => {
            let errors = {};

            if (dayjs(values.startDate, 'DD/MM/YYYY').isBefore(dayjs(), 'day')) {
                errors.startDate = 'Start date cannot be in the past';
            }

            if (dayjs(values.endTime, 'HH:mm').isBefore(dayjs(values.startTime, 'HH:mm'))) {
                errors.endTime = 'End time cannot be before start time';
            }

            if (
                dayjs(values.startDate, 'DD/MM/YYYY').isSame(dayjs(), 'day') &&
                dayjs(values.startTime, 'HH:mm').isBefore(dayjs(), 'minute')
            ) {
                errors.startTime = 'Start time cannot be in the past';
            }

            // New slot validation logic
            if (values.isEventHasSlot && values.slots?.length > 0) {
                errors.slots = [];

                const eventStartTime = dayjs(values.startTime, 'HH:mm');
                const eventEndTime = dayjs(values.endTime, 'HH:mm');

                values.slots?.forEach((slot, index) => {
                    const currentStartTime = dayjs(slot.startTime, 'HH:mm');
                    const currentEndTime = dayjs(slot.endTime, 'HH:mm');

                    // Initialize slot errors if needed
                    if (!errors.slots[index]) errors.slots[index] = {};

                    // 1. Start time related validations
                    // Ensure first slot Start Time is exactly same as Event Start Time
                    if (index === 0 && !currentStartTime.isSame(eventStartTime)) {
                        errors.slots[index].startTime =
                            'Start time should be same as event start time';
                    }

                    // Ensure slot Start Time is not before Event Start Time
                    if (currentStartTime.isBefore(eventStartTime)) {
                        errors.slots[index].startTime =
                            'Start time cannot be before event start time';
                    }

                    // Ensure slot Start Time is not after Event End Time
                    if (currentStartTime.isAfter(eventEndTime)) {
                        errors.slots[index].startTime = 'Start time cannot be after event end time';
                    }

                    // 2. End time related validations
                    // Ensure slot End Time is not before slot Start Time
                    if (currentEndTime.isBefore(currentStartTime)) {
                        errors.slots[index].endTime = 'End time cannot be before start time';
                    }

                    // Ensure slot End Time is not before Event Start Time
                    if (currentEndTime.isBefore(eventStartTime)) {
                        errors.slots[index].endTime = 'End time cannot be before event start time';
                    }

                    // Ensure slot End Time is not after Event End Time
                    if (currentEndTime.isAfter(eventEndTime)) {
                        errors.slots[index].endTime = 'End time cannot be after event end time';
                    }

                    // Check if current start time is before the previous slot's end time
                    if (index > 0) {
                        const previousSlot = values.slots[index - 1];
                        const previousEndTime = dayjs(previousSlot.endTime, 'HH:mm');

                        if (currentStartTime.isBefore(previousEndTime)) {
                            errors.slots[index].startTime =
                                'Selected time is overlapping with previous slot';
                        }
                    }

                    // Check for overlaps with all future slots
                    for (let i = index + 1; i < values.slots?.length; i++) {
                        const nextSlot = values.slots[i];
                        const nextStartTime = dayjs(nextSlot.startTime, 'HH:mm');
                        const nextEndTime = dayjs(nextSlot.endTime, 'HH:mm');

                        // Initialize next slot errors if needed
                        if (!errors.slots[i]) errors.slots[i] = {};

                        // If current end time overlaps with next slot's start time
                        if (nextStartTime.isBefore(currentEndTime)) {
                            errors.slots[i].startTime =
                                'Selected time is overlapping with previous slot';
                        }

                        // If next slot's end time overlaps with current slot's end time
                        if (nextEndTime.isSameOrBefore(currentEndTime)) {
                            errors.slots[i].endTime =
                                'Selected time is overlapping with previous slot';
                        }

                        // If there's no overlap with this slot, stop further checking
                        if (currentEndTime.isBefore(nextStartTime)) {
                            break;
                        }
                    }
                });

                // Clean up empty slot errors
                if (
                    errors.slots.length === 0 ||
                    errors.slots.every((slot) => !slot || Object.keys(slot).length === 0)
                ) {
                    delete errors.slots;
                }
            }

            return errors;
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);

                if (values.isEventHasSlot && values.slots?.length < 2) {
                    errorToast('At least two slots are required');
                    return false;
                }

                const payload = {
                    step: 'schedule',
                    user_id: user.id,
                    event_type: 'single_day',
                    start_date: dayjs(values.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    end_date: dayjs(values.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    start_time: dayjs(values.startTime, 'HH:mm').format('HH:mm'),
                    end_time: dayjs(values.endTime, 'HH:mm').format('HH:mm'),
                    schedules: [
                        {
                            date: dayjs(values.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                            from_time: dayjs(values.startTime, 'HH:mm').format('HH:mm:ss'),
                            to_time: dayjs(values.endTime, 'HH:mm').format('HH:mm:ss'),
                            slots: values.isEventHasSlot
                                ? values.slots?.map((slot) => ({
                                      from_time: dayjs(slot.startTime, 'HH:mm').format('HH:mm:ss'),
                                      to_time: dayjs(slot.endTime, 'HH:mm').format('HH:mm:ss')
                                  }))
                                : []
                        }
                    ]
                };

                // if (values.isEventHasSlot) {
                //     payload.schedules = [
                //         {
                //             date: dayjs(values.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                //             from_time: dayjs(values.startTime, 'HH:mm').format('HH:mm:ss'),
                //             to_time: dayjs(values.endTime, 'HH:mm').format('HH:mm:ss'),
                //             slots: values.slots.map(slot => ({
                //                 from_time: dayjs(slot.startTime, 'HH:mm').format('HH:mm:ss'),
                //                 to_time: dayjs(slot.endTime, 'HH:mm').format('HH:mm:ss'),
                //             }))
                //         }
                //     ]
                // }

                // console.log("Single Day Event Form Submitted", payload);

                if (hasSingleDayEventChanged(values, eventData)) {
                    await dispatch(
                        updateEventById({
                            eventId: eventData?.eventId,
                            eventData: payload
                        })
                    ).unwrap();
                }
                dispatch(nextTab());
            } catch (error) {
                errorToast(error?.message || 'Failed to update event schedule');
            } finally {
                setLoading(false);
            }
        }
    });

    // console.log("Errors : ", singleDayEventFormik.errors);

    // handle accordion open/close
    function handleExpandChange() {
        setExpanded(!expanded);
    }

    // function to add new slots and perform some validation
    function addSlot() {
        // stop if minute lower than 5
        if (minutes < 5) {
            return false;
        }

        // variable to store newly generated time
        let newStartTime;
        let newEndTime;

        // If this is the first slot being added then,
        // Set the start time of the first slot to match the event's start time
        // Calculate the end time by adding the selected duration (in minutes) to the start time
        if (singleDayEventFormik.values.slots?.length === 0) {
            newStartTime = dayjs(singleDayEventFormik.values.startTime, 'HH:mm');
            newEndTime = dayjs(singleDayEventFormik.values.startTime, 'HH:mm').add(
                minutes,
                'minute'
            );
        }

        // For subsequent slots, get the most recently added slot
        // Set the new slot's start time to match the previous slot's end time
        // This ensures slots are contiguous with no gaps
        // Calculate the end time by adding the selected duration to the start time
        else {
            const lastSlot =
                singleDayEventFormik.values.slots?.[singleDayEventFormik.values.slots?.length - 1];
            newStartTime = dayjs(lastSlot?.endTime, 'HH:mm');
            newEndTime = newStartTime.add(minutes, 'minute');
        }

        // Validate the new slot's start time against the event's end time
        if (dayjs(newEndTime).isAfter(dayjs(singleDayEventFormik.values.endTime, 'HH:mm'))) {
            errorToast('Modify slot durations, To add more slots.');
            return false;
        }

        // Create a new slot object with the calculated start and end times
        const newSlot = {
            id: singleDayEventFormik.values.slots?.length + 1,
            startTime: newStartTime,
            endTime: newEndTime
        };

        // Add the new slot to the formik state
        singleDayEventFormik.setFieldValue('slots', [
            ...singleDayEventFormik.values?.slots,
            newSlot
        ]);
    }

    // function to delete created slot based on indexing and update formik state
    function deleteSlot(index) {
        const updatedSlots = singleDayEventFormik.values?.slots?.filter((_, i) => i !== index);
        singleDayEventFormik.setFieldValue('slots', updatedSlots);
    }

    // function to handle slot duration change
    function handleSlotDurationChange(e) {
        const selectedValue = e.target.value;
        setSlotDuration(selectedValue);
        setMinutes(selectedValue);
        setCustomDuration('');
        setDurationError(false);
    }

    // function to handle the custom duration input change and perform execute conditions
    function handleCustomDurationChange(e) {
        const value = e.target.value;
        setCustomDuration(value);
        let durationInMinutes;
        if (hour === 'hour') {
            durationInMinutes = value * 60;
        } else {
            durationInMinutes = parseInt(value, 10);
        }
        setMinutes(durationInMinutes);
        setSlotDuration('');
        setDurationError(durationInMinutes < 5);
    }

    // console.log("Initial values -> ", initialValues)

    // return the needed states or functions
    return {
        hour,
        setHour,
        expanded,
        deleteSlot,
        slotDuration,
        durationError,
        customDuration,
        slotDurationOptions,
        singleDayEventFormik,
        addSlot,
        handleExpandChange,
        handleSlotDurationChange,
        handleCustomDurationChange,
        loading
    };
}

const validationSchema = Yup.object({
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().nullable(),
    startTime: Yup.string().required('Start time is required'),
    endTime: Yup.string().required('End time is required'),
    isEventHasSlot: Yup.boolean().default(false),
    slots: Yup.array()
        .of(
            Yup.object().shape({
                startTime: Yup.string().required('Start time is required'),
                endTime: Yup.string().required('End time is required')
            })
        )
        .when('isEventHasSlot', {
            is: true,
            then: (schema) => schema.required('At least two slots are required'),
            otherwise: (schema) => schema.notRequired()
        })
});

const slotDurationOptions = [
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 180, label: '3 hours' },
    { value: 240, label: '4 hours' },
    { value: 300, label: '5 hours' }
];
